import { PRE_LOGOUT } from "../graphql/mutation/mutation";
import { signalLogoutStarted } from "./logoutOverlay";

/**
 * Runs BE-side cleanup before the FE hands off to Cognito on logout.
 *
 * The FE handles logout by calling ``Auth.signOut`` directly — the BE
 * never sees that RPC. So per-user BE-side state (Redis permission
 * caches today, more in the future) silently lingers until its TTL
 * expires. This function fires the ``preLogout`` mutation so that
 * state clears with the actual logout instead of on the next login.
 *
 * Reads the Apollo client off ``window.__APOLLO_CLIENT__`` which the
 * authenticator provider exposes at client-create time. That lets
 * non-React call sites (errorBoundry, legacy helpers, gatsby plugins)
 * use the same code path without threading a client through every
 * caller. React components can also call this — they don't need the
 * usePreLogout hook variant unless they want explicit useApolloClient
 * dependency tracking.
 *
 * Best-effort: any failure (network down, BE 500, expired token, no
 * client on window) is swallowed. Logout itself never blocks.
 *
 * Usage:
 *   import { runPreLogout } from "../library/preLogout";
 *   await runPreLogout();
 *   Auth.signOut();
 */
export const runPreLogout = userId => {
  // Flip the overlay flag immediately so the UI doesn't sit in
  // empty-state limbo while Cognito / redirect resolve.
  signalLogoutStarted();
  // Fire-and-forget: the BE cleanup is best-effort and the FE
  // should not block on it. The caller's logout sequence (Auth.signOut
  // + storage clear + redirect) starts in parallel; if the BE
  // round-trip is slow or fails, the user is already gone. The
  // mutation itself doesn't need the auth header (PreLogout is in
  // ALLOWED_QUERIES) and takes the user id explicitly, so it
  // executes correctly even after sessionStorage has been wiped by
  // the caller mid-flight.
  //
  // Returns an already-resolved promise so the existing call sites'
  // ``.finally(() => Auth.signOut().then(...))`` chains run
  // immediately without waiting on the BE round-trip.
  try {
    if (typeof window === "undefined") return Promise.resolve();
    const numericUserId = Number(userId);
    if (!numericUserId) return Promise.resolve();
    // eslint-disable-next-line no-underscore-dangle
    const client = window.__APOLLO_CLIENT__;
    if (!client) return Promise.resolve();
    // Fire the mutation but DON'T await it — let it complete (or fail)
    // in the background. Swallow rejections so they don't show as
    // unhandled promise warnings after the page has navigated away.
    client
      .mutate({
        mutation: PRE_LOGOUT,
        variables: { userId: numericUserId },
        fetchPolicy: "no-cache",
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn("preLogout: BE cleanup skipped", err);
      });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("preLogout: setup error, skipping BE cleanup", err);
  }
  return Promise.resolve();
};

export default runPreLogout;
